import React, {
	createContext,
	useCallback,
	useContext,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import {
	embeddedViewToEditTransitionMetric,
	embeddedConfluenceIframeLoadMetrics,
	type LoadingMetricContentType,
} from './perf.config';

import {
	EMBEDDED_CONFLUENCE_METRICS_PREFIX,
	EMBEDDED_EDIT_CLICK,
	EMBEDDED_EDITOR_TTI,
	EMBEDDED_IFRAME_CONTENT_INITIALIZED,
	EMBEDDED_VIEW_TTI,
} from './constants';
import type { PageSegmentLoadMetric, Metric } from '@atlassian/browser-metrics/types';

type KeyName = 'lazy-load' | 'common-load' | 'iframe-content-initialized';
type MarkName = `${KeyName}-${'start' | 'end'}`;

type PerformanceMetricsContext = {
	cancelIframeLoadMetrics: () => void;
	isInitialized: boolean;
	markIframeLoadMetrics: (markName: MarkName) => void;
	setContentType: (contentType: LoadingMetricContentType) => void;
};

// PerformanceMetricsProvider is only applied to internal EP
// Methods provided by PerformanceMetricsContext are defined by default as () => {}
// This allows instances not wrapped in provider (ie embedded-confluence public) to opperate without errors
const PerformanceMetricsContext: React.Context<PerformanceMetricsContext> =
	createContext<PerformanceMetricsContext>({
		isInitialized: false,
		cancelIframeLoadMetrics: () => {},
		markIframeLoadMetrics: () => {},
		setContentType: () => {},
	});

// calculate timestamp from iframe's performance metric post message
// relative to parent's window.performance.now() adjusted for post message latency
const adjustedPerformanceTimestamp = (timestamp: any) =>
	typeof timestamp === 'number' ? window.performance.now() - (Date.now() - timestamp) : undefined;

const Provider = ({
	children,
	contentType,
	sourceComponent,
}: {
	children: React.ReactNode;
	contentType?: LoadingMetricContentType;
	sourceComponent?: string;
}) => {
	const [currentContentType, setCurrentContentType] = useState(contentType);
	const isInitialized = useRef(true);
	const iframeLoadMetrics = useRef<PageSegmentLoadMetric | undefined>();
	const embeddedViewToEditTransitionTimer = useRef<Metric | undefined>();

	useLayoutEffect(() => {
		// TODO: Currently EP does not work with more that one instance per parent window
		// When this limitation is fixed we will also need to find a way to serialize post messages
		// this is because post message listeners will pick up on all messages coming to the parent window
		// triggering any callback listening to a given data.type message
		// therefore messages will need to be scoped to the particular instance they are intended for

		embeddedViewToEditTransitionTimer.current?.cancel();
		embeddedViewToEditTransitionTimer.current = embeddedViewToEditTransitionMetric();

		// Setup iframe post message handling
		const handler = (e: MessageEvent) => {
			let uniqueKey;
			try {
				uniqueKey = new URL(window.location.href).searchParams.get('uniqueKey');
			} catch (error) {
				uniqueKey = null;
			}
			// Validate uniqueKey (if it exists) to avoid race condition between multiple embedded confluence instances
			if (
				!e.data.payload?.customData?.uniqueKey ||
				!uniqueKey ||
				e.data.payload.customData.uniqueKey === uniqueKey
			) {
				switch (e.data.type) {
					case EMBEDDED_CONFLUENCE_METRICS_PREFIX + EMBEDDED_VIEW_TTI:
						iframeLoadMetrics.current?.stop({
							stopTime: adjustedPerformanceTimestamp(e.data.payload?.timestamp),
							customData: sourceComponent ? { sourceComponent } : undefined,
						});
						break;
					case EMBEDDED_CONFLUENCE_METRICS_PREFIX + EMBEDDED_EDITOR_TTI:
						iframeLoadMetrics.current?.stop({
							stopTime: adjustedPerformanceTimestamp(e.data.payload?.timestamp),
							customData: sourceComponent ? { sourceComponent } : undefined,
						});
						embeddedViewToEditTransitionTimer.current?.stop({
							stopTime: adjustedPerformanceTimestamp(e.data.payload?.timestamp),
							customData: sourceComponent ? { sourceComponent } : undefined,
						});
						break;
					case EMBEDDED_CONFLUENCE_METRICS_PREFIX + EMBEDDED_EDIT_CLICK:
						embeddedViewToEditTransitionTimer.current?.start({
							startTime: adjustedPerformanceTimestamp(e.data.payload?.timestamp),
						});
						break;
					case EMBEDDED_CONFLUENCE_METRICS_PREFIX + EMBEDDED_IFRAME_CONTENT_INITIALIZED:
						iframeLoadMetrics.current?.mark(
							'iframe-content-initialized-end',
							adjustedPerformanceTimestamp(e.data.payload?.timestamp),
						);
						break;
				}
			}
		};
		window.addEventListener('message', handler);

		const currentTransitionTimer = embeddedViewToEditTransitionTimer.current;

		return () => {
			window.removeEventListener('message', handler);
			currentTransitionTimer.cancel();
		};
	}, [embeddedViewToEditTransitionTimer, sourceComponent]);

	// Set load metric based on content type
	useLayoutEffect(() => {
		if (currentContentType) {
			// cancel current Embedded Confluence iframe load metric if necessary
			iframeLoadMetrics.current?.cancel();
			iframeLoadMetrics.current = embeddedConfluenceIframeLoadMetrics(currentContentType);
			// start Embedded Confluence iframe load metric
			iframeLoadMetrics.current?.start();
			iframeLoadMetrics.current?.mark('lazy-load-start');
		}

		return () => {
			iframeLoadMetrics.current?.cancel();
		};
	}, [currentContentType, sourceComponent]);

	const cancelIframeLoadMetrics = useCallback(() => {
		iframeLoadMetrics.current?.cancel();
	}, []);

	const markIframeLoadMetrics = useCallback((markName: MarkName) => {
		iframeLoadMetrics.current?.mark(markName);
	}, []);

	return (
		<PerformanceMetricsContext.Provider
			value={{
				cancelIframeLoadMetrics,
				markIframeLoadMetrics,
				isInitialized: isInitialized.current,
				setContentType: setCurrentContentType,
			}}
		>
			{children}
		</PerformanceMetricsContext.Provider>
	);
};

export const usePerformanceMetrics = (): PerformanceMetricsContext => {
	return useContext(PerformanceMetricsContext);
};
/**
 * Performance metrics provider that manages provider initialization in a hierarchical component tree.
 *
 * Key features:
 * - Ensures single initialization: Only the topmost provider in the component tree becomes active
 * - Handles content type updates: Child components can update metrics via setContentType
 * - Prevents duplicate metrics: Automatically skips initialization if a parent provider exists
 *
 * Example: When ViewPage is rendered within Page component, provider is only initialized by Page
 */
export const PerformanceMetricsProvider = ({
	children,
	contentType,
	sourceComponent,
}: {
	children: React.ReactNode;
	contentType?: LoadingMetricContentType;
	sourceComponent?: string;
}): React.JSX.Element => {
	const { isInitialized, setContentType } = usePerformanceMetrics();
	useLayoutEffect(() => {
		// Update contentType when Page component replaces ViewPage with EditPage or vice versa
		isInitialized && contentType && setContentType(contentType);
	}, [isInitialized, contentType, setContentType]);
	return !isInitialized ? (
		<Provider contentType={contentType} sourceComponent={sourceComponent}>
			{children}
		</Provider>
	) : (
		<>{children}</>
	);
};
